import { yupResolver } from '@hookform/resolvers/yup';
import { walletsInstallmentSchema } from '@payments-checkout/shared/helpers/constants/validation-input';
import { InstallmentsManager } from '@payments-checkout/shared/helpers/index';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { Select, Text } from '@stone-payments/jade';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { usePageSharedData } from '../../hooks/usePageSharedData';

// Valor da option placeholder do Select do Jade; o RHF o lê do DOM quando o campo nasce sem valor.
const SELECT_PLACEHOLDER_VALUE = '__DEFAULT';

type ApplePayRequiredFieldsProps = {
  setIsDigitalWalletValid: (value: boolean) => void;
};

type ApplePayFormFields = {
  installments: string;
};

export const ApplePayRequiredFields = ({
  setIsDigitalWalletValid,
}: ApplePayRequiredFieldsProps) => {
  const { formatMessage } = useIntl();
  const {
    installments,
    setInstallments,
    setInstallmentsExplanationText,
    setSurchargesText,
  } = usePaymentMethod();

  const { installmentOptions } = usePageSharedData();

  const formattedInstallmentsOptions = InstallmentsManager.formatOptions(
    installmentOptions,
    formatMessage
  );
  const hasInstallments = formattedInstallmentsOptions.length > 0;

  const methods = useForm<ApplePayFormFields>({
    mode: 'onBlur',
    resolver: yupResolver(walletsInstallmentSchema),
    defaultValues: {
      installments: installments?.toString() || undefined,
    },
  });

  const selectInstallments = (value: string) =>
    InstallmentsManager.handleInstallmentsChange(
      value,
      formattedInstallmentsOptions,
      setInstallments,
      setInstallmentsExplanationText,
      setSurchargesText
    );

  useEffect(() => {
    if (formattedInstallmentsOptions.length === 1) {
      const [onlyOption] = formattedInstallmentsOptions;
      methods.setValue('installments', onlyOption.value, {
        shouldValidate: true,
      });
      selectInstallments(onlyOption.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedInstallmentsOptions.length]);

  const selectedInstallments = useWatch({
    control: methods.control,
    name: 'installments',
  });
  const hasSelection =
    !!selectedInstallments && selectedInstallments !== SELECT_PLACEHOLDER_VALUE;

  // Lido sempre: o RHF só acompanha formState.isValid se for acessado no render.
  const { isValid: isFormValid } = methods.formState;
  const isValid = !hasInstallments || (hasSelection && isFormValid);

  useEffect(() => {
    setIsDigitalWalletValid(isValid);
    return () => setIsDigitalWalletValid(false);
  }, [isValid, setIsDigitalWalletValid]);

  if (!hasInstallments) return null;

  const installmentsError = methods.formState.errors?.installments?.message;

  return (
    <FormProvider {...methods}>
      <div
        className="grid grid-cols-1 gap-jade-200 w-full"
        data-testid="apple-pay-required-fields"
      >
        <Text variant="heading-small" color="high">
          {formatMessage({ id: 'installmentsForm.title' })}
        </Text>
        <Select
          label={formatMessage({ id: 'creditCardForm.installments' })}
          required
          size="medium"
          placeholder={formatMessage({
            id: 'creditCardForm.installmentsSelect.placeholder',
          })}
          options={formattedInstallmentsOptions}
          disabled={formattedInstallmentsOptions.length === 1}
          error={!!installmentsError}
          supportText={
            installmentsError &&
            formatMessage({
              id: `creditCardFields.validate.${installmentsError}`,
            })
          }
          {...methods.register('installments', {
            onChange: (e) => selectInstallments(e.target.value),
          })}
        />
      </div>
    </FormProvider>
  );
};
